import type { MartinaEmailComposerReviewReader, MartinaEmailComposerApprovedSend } from "@shared/martinaEmailDraftApproval";
import { useCallback, useRef, useState } from "react";
import { Mic, Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useVoiceRecognition } from "@/hooks/useVoiceRecognition";
import { useMartinaGlobal } from "@/contexts/ExAiGlobalContext";

type EmailComposeMode = "compose" | "reply" | "forward";

interface EmailMartinaComposerBarProps {
  /** Read body only when generating — avoid live watch re-renders on every keystroke. */
  getCurrentBody: () => string;
  getSubject?: () => string;
  to?: string[];
  mode?: EmailComposeMode;
  emailMessageId?: number | null;
  onInsertDraft: (draft: string, mode: "append" | "replace") => void;
  /** Invio dell'email aperta nel composer, con i suoi allegati e destinatari.
   * Senza questo, "Inviala" nel drawer spedirebbe una seconda email povera e
   * lascerebbe qui quella vera (caso reale Rinaldi, 2 ago 2026). */
  onSendDraft?: MartinaEmailComposerApprovedSend;
  getSendReview?: MartinaEmailComposerReviewReader;
}

export function EmailMartinaComposerBar({
  getCurrentBody,
  getSubject = () => "",
  to = [],
  mode = "compose",
  emailMessageId,
  onInsertDraft,
  onSendDraft,
  getSendReview,
}: EmailMartinaComposerBarProps) {
  const { toast } = useToast();
  const composerHandlers = useRef({ onSendDraft, getSendReview });
  composerHandlers.current = { onSendDraft, getSendReview };
  const { openMartina } = useMartinaGlobal();
  const [instruction, setInstruction] = useState("");
  const [interimInstruction, setInterimInstruction] = useState("");
  const [draft, setDraft] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const instructionRef = useRef<HTMLTextAreaElement | null>(null);
  const { isSupported, isListening, error: voiceError, start, stop } = useVoiceRecognition(
    useCallback((finalText: string, interimText: string) => {
      setInstruction(finalText);
      setInterimInstruction(interimText);
    }, []),
    undefined,
    4_500,
    { focusTargetRef: instructionRef },
  );

  const currentInstruction = [instruction, interimInstruction].filter(Boolean).join(" ").trim();

  const generateDraft = async () => {
    if (isListening) stop();
    if (!currentInstruction) {
      toast({
        title: "Istruzione Martina mancante",
        description: "Scrivi o detta cosa vuoi preparare per questa email.",
        variant: "destructive",
      });
      return;
    }
    setIsGenerating(true);
    const subject = getSubject() || "";
    const currentBody = getCurrentBody() || "";
    openMartina({
      context: {
        terminalType: "email",
        entityType: emailMessageId ? "email_message" : "email_compose",
        entityId: emailMessageId ? String(emailMessageId) : "new",
        route: window.location.pathname,
        title: subject || "Composer Email",
        composeMode: mode,
        composeSubject: subject,
        composeTo: to,
        composeBody: currentBody,
        // Il click su "Genera" e' gia' una richiesta esplicita di bozza:
        // il server non deve scartare il testo libero da migliorare solo
        // perche' non contiene verbi come "scrivi/invia".
        composerAssist: true,
      },
      prompt: currentInstruction,
      autoSubmitPrompt: true,
      onEmailReplyDraft: (result) => { setDraft(result.body); setIsGenerating(false); },
      // "Inviala" nel drawer deve spedire QUESTA email, allegati compresi, non
      // crearne una seconda dal solo testo della bozza.
      onEmailComposerSend: onSendDraft ? (body, fingerprint) => composerHandlers.current.onSendDraft?.(body, fingerprint) ?? false : undefined,
      getEmailComposerSendReview: getSendReview ? (body) => composerHandlers.current.getSendReview?.(body) ?? null : undefined,
    });
    setIsGenerating(false);
  };

  return (
    <div className="border border-primary/20 bg-primary/5 px-2.5 py-2">
      <div className="flex min-w-0 items-center gap-2 text-xs">
        <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
        <span className="shrink-0 font-semibold text-primary">Martina</span>
        <textarea
          ref={instructionRef}
          value={isListening ? currentInstruction : instruction}
          onChange={(event) => setInstruction(event.target.value)}
          readOnly={isListening}
          placeholder={'Detta o scrivi cosa deve preparare Martina. Es. "scrivi una risposta formale in inglese: ci possiamo vedere a pranzo lunedi 17 agosto a Milano"'}
          className="min-h-9 min-w-0 flex-1 resize-y border border-border bg-background px-2 py-1.5 text-xs text-foreground outline-none placeholder:text-muted-foreground"
        />
        <button
          type="button"
          onClick={isListening ? stop : start}
          disabled={!isSupported || isGenerating}
          className={[
            "inline-flex h-9 shrink-0 items-center gap-1.5 border px-2 text-xs font-medium disabled:opacity-50",
            isListening ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-foreground hover:bg-muted",
          ].join(" ")}
          title={isSupported ? "Detta istruzioni a Martina" : "Dettatura non supportata in questo browser"}
        >
          <Mic className={["h-3.5 w-3.5", isListening ? "animate-pulse" : ""].join(" ")} />
          {isListening ? "Stop" : "Detta"}
        </button>
        <button
          type="button"
          onClick={generateDraft}
          disabled={isGenerating}
          className="h-9 shrink-0 border border-primary bg-primary px-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {isGenerating ? "Genero" : "Genera"}
        </button>
      </div>
      {voiceError && <div className="mt-1 text-xs text-destructive">{voiceError}</div>}
      {draft && (
        <div className="mt-2 border border-border bg-background p-2">
          <div className="flex min-w-0 items-center justify-between gap-2">
            <div className="text-xs font-semibold text-foreground">Proposta Martina</div>
            <div className="flex shrink-0 gap-1.5">
              <button type="button" onClick={() => onInsertDraft(draft, "append")} className="border border-border bg-background px-2 py-1 text-xs hover:bg-muted">
                Aggiungi al corpo
              </button>
              <button type="button" onClick={() => onInsertDraft(draft, "replace")} className="border border-primary bg-primary/5 px-2 py-1 text-xs text-primary hover:bg-primary/10">
                Sostituisci corpo
              </button>
            </div>
          </div>
          <pre className="mt-2 max-h-28 overflow-y-auto whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">{draft}</pre>
        </div>
      )}
    </div>
  );
}
